;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type {
  AnalyticsAccountType,
  AnalyticsB2cPlan,
  AnalyticsDashboardGroup,
  AnalyticsRoleContext,
  AnalyticsRoleInput,
} from './types';

// Subset of TEAM_ROLES from packages/ui/src/lib/constants.ts — kept local to avoid
// a circular dependency (@workspace/ui already imports @workspace/analytics).
const ANALYTICS_TEAM_ROLES = {
  TEAM_LEAD: 'teams_team_lead',
} as const;

// Local copies of ACCESSIBLE_APPS / USER_ROLES / B2C_ROLES from @workspace/ui.
// Importing that package would create a circular dependency.
const ANALYTICS_ACCESSIBLE_APPS = {
  ADMIN: 'admin',
  EMPLOYEE: 'employee',
  CONTRACTOR: 'contractor',
  EMPLOYER: 'employer',
  WALLET: 'wallet',
  GIG_WORKER: 'gig_worker',
} as const;

const ANALYTICS_EMPLOYER_ROLES = ['employer', 'organisation_owner'] as const;
const ANALYTICS_CONTRACTOR_ROLES = [
  'individual_contractor',
  'company_contractor',
  'contractor_of_record',
] as const;
const ANALYTICS_GIG_WORKER_ROLE = 'gig_worker';
const ANALYTICS_ADMIN_ROLE = 'admin';

// Employees also hold b2c_standard, so b2c_employee must be resolved first
// (mirrors getCurrentB2cRole in apps/admin-app/lib/b2c-role.ts).
const ANALYTICS_B2C_PLAN_PRIORITY: readonly AnalyticsB2cPlan[] = [
  'b2c_employee',
  'b2c_standard',
  'b2c_free',
];

const ANALYTICS_EMPLOYEE_ROLES = [
  'employee',
  'payroll_seat',
  'draft_seat',
  'teams_global_admin',
  ...ANALYTICS_B2C_PLAN_PRIORITY,
] as const;

// Deterministic primary role: the first role in this list the user holds wins,
// regardless of the order the backend returns roles in.
const ANALYTICS_PRIMARY_ROLE_PRIORITY: readonly string[] = [
  ...ANALYTICS_EMPLOYER_ROLES,
  ...ANALYTICS_CONTRACTOR_ROLES,
  ANALYTICS_GIG_WORKER_ROLE,
  ...ANALYTICS_EMPLOYEE_ROLES,
  ANALYTICS_ADMIN_ROLE,
];

// Product-confirmed role → dashboard bucket mapping. Roles outside this table
// (e.g. admin, finance, legal) intentionally resolve to 'unmapped'.
const ROLE_TO_DASHBOARD_GROUP: Record<string, AnalyticsDashboardGroup> = {
  employee: 'employee',
  employer: 'employer',
  individual_contractor: 'contractor',
  company_contractor: 'contractor',
  contractor_of_record: 'contractor',
  gig_worker: 'employee',
  organisation_owner: 'employer',
  payroll_seat: 'employee',
  draft_seat: 'employee',
  teams_global_admin: 'employee',
  b2c_employee: 'employee',
  b2c_standard: 'employee',
  b2c_free: 'employee',
};

function flattenTeamRoles(teams: AnalyticsRoleInput['teams']): string[] {
  if (!teams?.length) return [];

  const roles = new Set<string>();
  for (const team of teams) {
    for (const role of team.team_role ?? []) {
      roles.add(role);
    }
  }

  return [...roles];
}

function normalizeRoles(roles: string[] | undefined): string[] {
  return (roles ?? []).map((role) => role.trim()).filter(Boolean);
}

function hasAnyRole(roles: string[], candidates: readonly string[]): boolean {
  return candidates.some((role) => roles.includes(role));
}

// Priority: employer > contractor > gig worker > employee > admin.
// Contractor roles win over an `employee` accessible_app, matching product navigation.
// B2C wallet users are 'employee'; use `b2c_plan` to segment them further.
export function resolveAnalyticsAccountType(
  accessibleApp?: string,
  roles?: string[]
): AnalyticsAccountType {
  const normalizedRoles = normalizeRoles(roles);

  if (
    accessibleApp === ANALYTICS_ACCESSIBLE_APPS.EMPLOYER ||
    hasAnyRole(normalizedRoles, ANALYTICS_EMPLOYER_ROLES)
  ) {
    return 'employer';
  }

  if (
    accessibleApp === ANALYTICS_ACCESSIBLE_APPS.CONTRACTOR ||
    hasAnyRole(normalizedRoles, ANALYTICS_CONTRACTOR_ROLES)
  ) {
    return 'contractor';
  }

  if (
    accessibleApp === ANALYTICS_ACCESSIBLE_APPS.GIG_WORKER ||
    normalizedRoles.includes(ANALYTICS_GIG_WORKER_ROLE)
  ) {
    return 'gig_worker';
  }

  if (
    accessibleApp === ANALYTICS_ACCESSIBLE_APPS.EMPLOYEE ||
    accessibleApp === ANALYTICS_ACCESSIBLE_APPS.WALLET ||
    hasAnyRole(normalizedRoles, ANALYTICS_EMPLOYEE_ROLES)
  ) {
    return 'employee';
  }

  if (
    accessibleApp === ANALYTICS_ACCESSIBLE_APPS.ADMIN ||
    normalizedRoles.includes(ANALYTICS_ADMIN_ROLE)
  ) {
    return 'admin';
  }

  return 'unmapped';
}

export function resolveAnalyticsB2cPlan(roles?: string[]): AnalyticsB2cPlan | undefined {
  const normalizedRoles = normalizeRoles(roles);
  return ANALYTICS_B2C_PLAN_PRIORITY.find((plan) => normalizedRoles.includes(plan));
}

// Falls back to the alphabetically first role so unknown roles stay deterministic.
export function resolveAnalyticsPrimaryRole(roles?: string[]): string | undefined {
  const normalizedRoles = normalizeRoles(roles);
  return (
    ANALYTICS_PRIMARY_ROLE_PRIORITY.find((role) => normalizedRoles.includes(role)) ??
    [...normalizedRoles].sort()[0]
  );
}

export function resolveAnalyticsRoleContext(input: AnalyticsRoleInput): AnalyticsRoleContext {
  const roles = input.roles ?? [];
  const teams = input.teams ?? [];
  const teamRoles = flattenTeamRoles(teams);
  const isTeamLead = teamRoles.includes(ANALYTICS_TEAM_ROLES.TEAM_LEAD);
  const primaryRole = resolveAnalyticsPrimaryRole(roles);

  return {
    roles,
    team_roles: teamRoles,
    is_team_lead: isTeamLead,
    organisation_id: input.organisationId ?? undefined,
    team_count: teams.length,
    // Deprecated for segmentation: roles[0] depends on backend role order. Kept so
    // existing insights keep working; use primary_role / account_type / b2c_plan.
    people_role: roles[0]?.trim() || undefined,
    primary_role: primaryRole,
    dashboard_group: (primaryRole && ROLE_TO_DASHBOARD_GROUP[primaryRole]) || 'unmapped',
    account_type: resolveAnalyticsAccountType(input.accessibleApp, roles),
    b2c_plan: resolveAnalyticsB2cPlan(roles),
  };
}
